<script setup lang="ts">
  import dayjs from 'dayjs';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import type { EmailThread, Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    task?: Task;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);

  const componentTask = computed(() => props.task ?? currentTask.value);
  const displayCloseReason = computed(() =>
    componentTask.value?.close_reason?.split('%%%')[0]?.trim(),
  );

  const closeReasonMeta = computed(() => {
    const raw = componentTask.value?.close_reason;
    if (!raw?.includes('%%%')) return null;

    const metaPart = raw.split('%%%')[1]?.trim();
    if (!metaPart) return null;

    const pairs: Record<string, string> = {};
    for (const token of metaPart.split(/\s+/)) {
      const idx = token.indexOf('#');
      if (idx > 0) {
        pairs[token.slice(0, idx)] = token.slice(idx + 1);
      }
    }

    return pairs;
  });

  const sourceTaskId = computed(() => closeReasonMeta.value?.source_task_id);
  const sourceEmailId = computed(() => closeReasonMeta.value?.email_id);

  const handleOpenSourceTask = () => {
    if (!sourceTaskId.value) return;
    taskCollectionStore.openTaskByQuery?.({ id: sourceTaskId.value }, route.name as string);
  };

  const emailThread = ref<EmailThread>();
  const showEmailThread = ref(false);

  const handleOpenEmail = async () => {
    if (!sourceEmailId.value) return;

    const email = await stuut.emails.retrieve(sourceEmailId.value);
    if (!email?.thread_id) return;

    emailThread.value = await stuut.emailThreads.retrieve(email.thread_id);
    showEmailThread.value = true;
  };
</script>

<template>
  <div
    v-if="componentTask?.id && !componentTask?.isOpen"
    class="flex flex-col lg:flex-row lg:items-center justify-between w-full"
  >
    <div class="flex items-center gap-x-3.5 flex-1 w-full">
      <i
        class="fas text-xl h-[21px] w-[21px]"
        :class="{
          'fa-box-archive': componentTask?.isArchived,
          'fa-ban': componentTask?.isClosed,
          'fa-check-circle text-success': componentTask?.isCompleted,
        }"
      ></i>

      <div class="leading-xs w-full">
        <Tooltip :disabled="!componentTask?.isClosed || !!displayCloseReason">
          <TooltipTrigger class="flex gap-x-2.5 items-baseline w-full">
            <strong> Task {{ componentTask?.status }} </strong>

            <span class="flex items-center justify-between text-muted-foreground flex-1">
              <span>{{ displayCloseReason || componentTask?.close_reason }}</span>

              <div v-if="sourceTaskId || sourceEmailId" class="flex gap-x-4">
                <Button
                  v-if="sourceTaskId"
                  variant="link"
                  size="inline"
                  class="text-muted-foreground underline"
                  @click.stop="handleOpenSourceTask"
                >
                  View source task
                </Button>

                <Button
                  v-if="sourceEmailId"
                  variant="link"
                  size="inline"
                  class="text-muted-foreground underline"
                  @click.stop="handleOpenEmail"
                >
                  View email
                </Button>
              </div>
            </span>
          </TooltipTrigger>

          <TooltipContent class="max-w-[500px]">
            <p v-if="componentTask?.isClosed">
              {{ displayCloseReason || componentTask?.close_reason }}
            </p>
          </TooltipContent>
        </Tooltip>

        <span v-if="componentTask?.completed_action?.user?.name">
          by {{ componentTask?.completed_action?.user?.name }}
        </span>

        <span v-if="componentTask?.completed_action?.created_at">
          on

          <Timestamp
            :open-delay="500"
            :with-conversion-table="true"
            :time="dayjs(componentTask?.completed_action?.created_at)"
            local
            format="M/D/YYYY h:mma"
          />
        </span>
      </div>
    </div>

    <div
      v-if="componentTask?.completed_action?.created_at"
      class="flex items-center space-x-1 ml-10 lg:ml-0"
    >
      <span v-if="componentTask?.timeToCompletion" class="italic">
        <template v-if="componentTask?.isClosed">Closed task</template>
        <template v-else-if="componentTask?.isCompleted">
          <template v-if="componentTask?.isReplyRequired">Responded</template>
          <template v-else>Resolved</template>
        </template>

        in {{ componentTask?.timeToCompletion }}
      </span>
    </div>

    <EmailThreadDrawer
      v-if="emailThread"
      v-model:open="showEmailThread"
      :thread="emailThread"
      :selected-email-id="sourceEmailId"
      is-slider
      default-open
      class="h-full min-h-full"
      @close="showEmailThread = false"
    />
  </div>
</template>

<style></style>
